import * as React from 'react';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import { GroupGetServiceResponse, RateReliefCalculatorServiceResponse } from '../../services/api';
import { asCurrencyFormat, asPercentageValue } from '../../utils/parse';
import { classNames } from 'primereact/utils';
import BooleanBadge from '../ui/BooleanBadge';

export default function PlanDetailsModal({ group, rates, onHide, visible }: { group: GroupGetServiceResponse, rates: RateReliefCalculatorServiceResponse, visible: boolean, onHide: () => void }) {
  const premiumDiff = React.useMemo(() => {
    return group.policyDetailModel.newPremiumEquivalent! - group.policyDetailModel.currentPremiumEquivalent!;
  }, [group]);

  return (
    <Dialog visible={visible} onHide={onHide} header="Plan details" draggable={false}>
      <div className="grid gap-4">
        <section className="grid gap-3">
          <div className="font-bold">Employee med count</div>
          <div className="grid grid-cols-6 gap-2">
            <div className="p-4 text-sm text-center bg-gray-100 rounded">
              <strong className="text-grey-8">Employee Only</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountEE}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Emloyee<br /> + Spouse</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountES}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Employee<br /> + Child</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountEC}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Family</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountFF}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Total</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountTOT}</div>
            </div>
            <div className="flex flex-col justify-between p-4 text-sm text-center bg-gray-100 rounded">
              <strong className="text-grey-8">Old total</strong> <div className="text-lg font-medium">{group.policyDetailModel.employeeMedCountOldTOT}</div>
            </div>
          </div>
        </section>

        <section className="grid gap-3">
          <div className="[ font-bold ]">Cost analysis</div>
          <div className="[ grid grid-cols-2 gap-2 ]">
            <div className="[ flex flex-col items-start gap-2 bg-gray-100 rounded p-4 text-sm border border-allstate-blue ]">
              <div>
                <div className="text-sm font-bold text-grey-8">Current premium</div>
                <div className="[ font-medium text-lg ]">{asCurrencyFormat(group.policyDetailModel.currentPremiumEquivalent!)}</div>
              </div>
            </div>
            <div
              className={
                classNames(
                  "flex flex-col items-start gap-2 bg-gray-100 rounded p-4 text-sm  border border-allstate-blue ]"
                )
              }
            >
              <div>
                <div className="text-sm font-bold text-grey-8">New premium</div>
                <div className="[ font-medium text-lg ]">{asCurrencyFormat(group.policyDetailModel?.newPremiumEquivalent!)}</div>
              </div>

              {premiumDiff !== 0 && (
                <Badge
                  severity={premiumDiff > 0 ? "danger" : "success"}
                  value={
                    <span className="flex items-center gap-1">
                      <span className={
                        classNames(
                          "text-2xs pi",
                          {
                            "pi-arrow-up": premiumDiff > 0,
                            "pi-arrow-down": premiumDiff < 0,
                          }
                      )}></span>
                      <span>{asCurrencyFormat(Math.abs(premiumDiff))}</span>
                      <span>
                        {asPercentageValue(group.policyDetailModel.total * 100)} {premiumDiff > 0 ? 'increase' : 'decrease'}
                      </span>
                    </span>
                  }
                />
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-3">
          <div className="[ font-bold ]">Breakdown</div>
          <div className="[ grid grid-cols-6 gap-2 ]">
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Trend</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.trend * 100, 1)}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Census</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.sizeAndCensus * 100, 1)}</div>
            </div>
            <div className="[ flex flex-col justify-between flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">MARA Impact</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.maraImpact * 100, 1)}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Aggressive Rating</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.aggressiveRating * 100, 1)}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Other</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.other * 100, 1)}</div>
            </div>
            <div className="[ flex flex-col justify-between bg-gray-100 outline outline-1 outline-allstate-blue rounded p-4 text-center text-sm ]">
              <strong className="text-grey-8">Total</strong> <div className="[ font-bold text-lg ]">{asPercentageValue(group.policyDetailModel?.total * 100, 1)}</div>
            </div>
          </div>
        </section>

        <section className="grid gap-3">
          <div className="[ font-bold ]">Plan configuration</div>
          <div className="[ grid grid-cols-2 gap-2 ]">
            <div className="[ flex gap-2 border-b border-gray-200 py-2 ]">
              <div className="[ flex-1 ]">Prior relief</div>
              <div>{asPercentageValue(group.policyDetailModel?.priorRelief * 100)}</div>
            </div>
            <div className="[ flex gap-2 border-b border-gray-200 py-2 ]">
              <div className="[ flex-1 ]">Targeted group</div>
              <div>
                <BooleanBadge value={group.policyDetailModel?.targetedGroup} />
              </div>
            </div>
            <div className="[ flex gap-2 py-2 ]">
              <div className="[ flex-1 ]">Override</div>
              <div>
                <BooleanBadge value={group.policyDetailModel?.override} />
              </div>
            </div>
            <div className="[ flex gap-2 py-2 ]">
              <div className="[ flex-1 ]">Options shutoff</div>
              <div>
                <BooleanBadge value={group.policyDetailModel?.optionsShutoff} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  )
}